import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Grid,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Switch,
} from '@mui/material';
import {
    Download as DownloadIcon,
    People as PeopleIcon,
    DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { useTypyKontrolQuery, useToggleTypDashboardMutation } from '../../../api/JsdhNotifikaceService/service/KontrolyMutations';
import JsdhNotifikaceService from '../../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import { useToast } from '../../../components/toast/useToast';
import type { Konfigurace } from '../../../api/JsdhNotifikaceService/generated';
import type { useUpdateAdminKonfiguraceMutation } from '../../../api/JsdhNotifikaceService/service/AdminMutations';

// === DashboardTypyKontrolSection ===
const DashboardTypyKontrolSection: React.FC = () => {
    const { data: typyKontrol } = useTypyKontrolQuery();
    const toggleMutation = useToggleTypDashboardMutation();

    const uzivatelTypy = typyKontrol?.filter(t => t.target === 'UZIVATEL' || !t.target) ?? [];
    const technikaTypy = typyKontrol?.filter(t => t.target === 'TECHNIKA') ?? [];

    if (!typyKontrol || typyKontrol.length === 0) return null;

    const renderGroup = (
        title: string,
        icon: React.ReactNode,
        typy: typeof uzivatelTypy,
    ) => (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                p: 2,
                flex: 1,
                minWidth: 240,
            }}
        >
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                {icon}
                <Typography variant="subtitle2">{title}</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {typy.map(typ => (
                    <FormControlLabel
                        key={typ.id}
                        sx={{ ml: 0, mr: 0 }}
                        control={
                            <Switch
                                size="small"
                                checked={typ.zobrazitDashboard}
                                onChange={(e) => {
                                    toggleMutation.mutate({ id: typ.id, zobrazitDashboard: e.target.checked });
                                }}
                                disabled={toggleMutation.isPending}
                            />
                        }
                        label={<Typography variant="body2">{typ.nazev}</Typography>}
                    />
                ))}
            </Box>
        </Box>
    );

    return (
        <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                Jednotlivé typy kontrol na dashboardu
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {uzivatelTypy.length > 0 && renderGroup(
                    'Kontroly uživatelů',
                    <PeopleIcon fontSize="small" color="action" />,
                    uzivatelTypy,
                )}
                {technikaTypy.length > 0 && renderGroup(
                    'Kontroly techniky',
                    <CarIcon fontSize="small" color="action" />,
                    technikaTypy,
                )}
            </Box>
        </>
    );
};

// === PdfSettingsSection ===
interface PdfSettingsSectionProps {
    konfigurace: Konfigurace[] | undefined;
    updateKonfiguraceMutation: ReturnType<typeof useUpdateAdminKonfiguraceMutation>;
}

const PdfSettingsSection: React.FC<PdfSettingsSectionProps> = ({
    konfigurace,
    updateKonfiguraceMutation,
}) => {
    const [previewLoading, setPreviewLoading] = useState(false);
    const { showToast } = useToast();

    const pdfFormat = konfigurace?.find(k => k.klic === 'PDF_FORMAT')?.hodnota || 'LANDSCAPE';
    const pdfShowMap = konfigurace?.find(k => k.klic === 'PDF_SHOW_MAP')?.hodnota === 'true';
    const mapZoom = konfigurace?.find(k => k.klic === 'PDF_MAP_ZOOM')?.hodnota || '17';

    const handleFormatChange = (value: string) => {
        updateKonfiguraceMutation.mutate({ klic: 'PDF_FORMAT', hodnota: value });
    };

    const handleShowMapChange = (checked: boolean) => {
        updateKonfiguraceMutation.mutate({ klic: 'PDF_SHOW_MAP', hodnota: checked ? 'true' : 'false' });
    };

    const handleMapZoomChange = (value: string) => {
        updateKonfiguraceMutation.mutate({ klic: 'PDF_MAP_ZOOM', hodnota: value });
    };

    const handleDownloadPreview = async () => {
        setPreviewLoading(true);
        try {
            await JsdhNotifikaceService.downloadPdfPreview();
        } catch {
            showToast('Nepodařilo se stáhnout vzorové PDF.', 'error');
        } finally {
            setPreviewLoading(false);
        }
    };

    return (
        <Paper sx={{ mb: 3, p: 2 }}>
            <Typography variant="h6" gutterBottom>
                Nastavení PDF tisku
            </Typography>
            <Grid container spacing={3} alignItems="center">
                <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Formát PDF</InputLabel>
                        <Select
                            value={pdfFormat}
                            label="Formát PDF"
                            onChange={(e) => handleFormatChange(e.target.value)}
                            disabled={updateKonfiguraceMutation.isPending}
                        >
                            <MenuItem value="LANDSCAPE">Na šířku (Landscape)</MenuItem>
                            <MenuItem value="PORTRAIT">Na výšku (Portrait)</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={pdfShowMap}
                                onChange={(e) => handleShowMapChange(e.target.checked)}
                                disabled={updateKonfiguraceMutation.isPending}
                            />
                        }
                        label="Zobrazit mapu v PDF"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Přiblížení mapy</InputLabel>
                        <Select
                            value={mapZoom}
                            label="Přiblížení mapy"
                            onChange={(e) => handleMapZoomChange(e.target.value)}
                            disabled={updateKonfiguraceMutation.isPending || !pdfShowMap}
                        >
                            <MenuItem value="13">13 - Město</MenuItem>
                            <MenuItem value="14">14</MenuItem>
                            <MenuItem value="15">15</MenuItem>
                            <MenuItem value="16">16</MenuItem>
                            <MenuItem value="17">17 - Ulice</MenuItem>
                            <MenuItem value="18">18 - Detailní</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={previewLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
                        onClick={handleDownloadPreview}
                        disabled={previewLoading}
                    >
                        {previewLoading ? 'Generuji...' : 'Stáhnout vzor PDF'}
                    </Button>
                </Grid>
            </Grid>
        </Paper>
    );
};

// === OstatniTab ===
interface OstatniTabProps {
    konfigurace: Konfigurace[] | undefined;
    updateKonfiguraceMutation: ReturnType<typeof useUpdateAdminKonfiguraceMutation>;
    refetchKonfigurace: () => void;
}

const OstatniTab: React.FC<OstatniTabProps> = ({
    konfigurace,
    updateKonfiguraceMutation,
    refetchKonfigurace,
}) => {
    return (
        <>
            {/* Upozornění na dashboardu */}
            <Paper sx={{ mb: 3, p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    Upozornění na dashboardu
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Nastavení zobrazení varovných bannerů na hlavním dashboardu administrace.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={konfigurace?.find(k => k.klic === 'dashboard_upozorneni_kontroly')?.hodnota !== '0'}
                                onChange={(e) => {
                                    updateKonfiguraceMutation.mutate(
                                        { klic: 'dashboard_upozorneni_kontroly', hodnota: e.target.checked ? '1' : '0' },
                                        { onSuccess: () => refetchKonfigurace() }
                                    );
                                }}
                                disabled={updateKonfiguraceMutation.isPending}
                            />
                        }
                        label="Zobrazit upozornění na kontroly a povinnosti"
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={konfigurace?.find(k => k.klic === 'dashboard_upozorneni_technika_kontroly')?.hodnota !== '0'}
                                onChange={(e) => {
                                    updateKonfiguraceMutation.mutate(
                                        { klic: 'dashboard_upozorneni_technika_kontroly', hodnota: e.target.checked ? '1' : '0' },
                                        { onSuccess: () => refetchKonfigurace() }
                                    );
                                }}
                                disabled={updateKonfiguraceMutation.isPending}
                            />
                        }
                        label="Zobrazit upozornění na kontroly techniky"
                    />
                </Box>
                <DashboardTypyKontrolSection />
            </Paper>

            {/* PDF nastavení */}
            <PdfSettingsSection
                konfigurace={konfigurace}
                updateKonfiguraceMutation={updateKonfiguraceMutation}
            />
        </>
    );
};

export default OstatniTab;
